import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, Package, RotateCcw, Heart, MapPin, LogOut, Plus, Trash2, Star } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/context/StoreContext";
import ProductCard from "@/components/store/ProductCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PAY = { pending: "Pending", payment_initiated: "Payment Initiated", paid: "Paid", failed: "Failed", cod_pending: "COD Pending", cod_paid: "COD Paid", cancelled: "Cancelled" };
const TABS = [["profile", "Profile", User], ["orders", "My Orders", Package], ["returns", "My Returns", RotateCcw], ["wishlist", "Wishlist", Heart], ["addresses", "Addresses", MapPin]];

export default function Account() {
  const { user, logout, loadMe } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "profile";

  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);
  if (!user) return <div className="py-24 text-center text-sharoa-muted">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl mb-8">Hi, {user.name?.split(" ")[0]} ♡</h1>
      <div className="grid lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {TABS.map(([id, label, Icon]) => (
              <button key={id} data-testid={`account-tab-${id}`} onClick={() => setSp({ tab: id })}
                className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap ${tab === id ? "bg-sharoa-pale text-sharoa-rose font-medium" : "text-sharoa-charcoal hover:bg-sharoa-ivory"}`}>
                <Icon size={17} /> {label}
              </button>
            ))}
            <button onClick={logout} data-testid="account-logout" className="flex items-center gap-3 px-4 py-3 text-sm text-sharoa-muted hover:bg-sharoa-ivory"><LogOut size={17} /> Logout</button>
          </nav>
        </aside>
        <div className="lg:col-span-3">
          {tab === "profile" && <Profile user={user} onSave={loadMe} />}
          {tab === "orders" && <Orders />}
          {tab === "returns" && <Returns />}
          {tab === "wishlist" && <Wishlist />}
          {tab === "addresses" && <Addresses user={user} onSave={loadMe} />}
        </div>
      </div>
    </div>
  );
}

function Profile({ user, onSave }) {
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "" });
  const save = async () => { try { await api.put("/profile", f); toast("Profile updated ♡"); onSave(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="max-w-md space-y-4">
      <h2 className="font-serif text-2xl">Profile</h2>
      <label className="block"><span className="text-xs uppercase tracking-widest text-sharoa-muted">Name</span>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full border border-sharoa-line px-3 py-2.5 text-sm mt-1" /></label>
      <label className="block"><span className="text-xs uppercase tracking-widest text-sharoa-muted">Email</span>
        <input value={user.email} disabled className="w-full border border-sharoa-line px-3 py-2.5 text-sm mt-1 bg-sharoa-ivory" /></label>
      <label className="block"><span className="text-xs uppercase tracking-widest text-sharoa-muted">Mobile</span>
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full border border-sharoa-line px-3 py-2.5 text-sm mt-1" /></label>
      <button data-testid="profile-save" onClick={save} className="bg-sharoa-rose text-white text-xs uppercase tracking-widest px-8 py-3">Save</button>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  const openDetail = async (id) => { const r = await api.get(`/orders/${id}`); setDetail(r.data); };
  return (
    <div>
      <h2 className="font-serif text-2xl mb-5">My Orders</h2>
      {orders.length === 0 ? <p className="text-sharoa-muted text-sm">No orders yet. <Link to="/shop" className="text-sharoa-rose">Start shopping ♡</Link></p> : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className="border border-sharoa-line p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <div><p className="font-mono text-sm">#{o.order_number}</p><p className="text-xs text-sharoa-muted">{new Date(o.created_at).toLocaleDateString("en-IN")}</p></div>
                <div className="text-right"><p className="font-mono text-sm">{money(o.total)}</p><p className="text-xs text-sharoa-rose capitalize">{o.status}</p></div>
              </div>
              <div className="flex gap-2 mt-3">{o.items.slice(0, 4).map((it) => <img key={it.sku} src={it.image} alt="" className="w-12 h-16 object-cover bg-sharoa-ivory" />)}</div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="bg-sharoa-ivory px-2 py-1">{o.payment_method === "cod" ? "COD" : "Online"}</span>
                <span className="bg-sharoa-ivory px-2 py-1">{PAY[o.payment_status]}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openDetail(o.id)} data-testid={`view-order-${o.order_number}`} className="border border-sharoa-charcoal text-xs uppercase tracking-widest px-4 py-2">View Order</button>
                <Link to={`/track-order?order=${o.order_number}`} className="border border-sharoa-line text-xs uppercase tracking-widest px-4 py-2">Track</Link>
              </div>
            </div>
          ))}
        </div>
      )}
      <OrderDetail order={detail} onClose={() => setDetail(null)} onReturn={() => { setDetail(null); }} />
    </div>
  );
}

const REASONS = ["Size/Fit Issue", "Wrong Product Received", "Damaged Product", "Defective Product", "Product Not as Expected", "Other"];

function OrderDetail({ order, onClose, onReturn }) {
  const [ret, setRet] = useState(null); // item selected for return
  const [form, setForm] = useState({ reason: REASONS[0], resolution: "refund", comments: "" });
  if (!order) return null;

  const submitReturn = async () => {
    try {
      await api.post("/returns", { order_id: order.id, items: [{ product_id: ret.product_id, sku: ret.sku, quantity: ret.quantity }],
        reason: form.reason, resolution: form.resolution, comments: form.comments, photos: [] });
      toast("Return request submitted ♡"); setRet(null); onReturn();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Order #{order.order_number}</DialogTitle></DialogHeader>
        {!ret ? (
          <div className="space-y-4">
            <div className="text-xs text-sharoa-muted">Placed {new Date(order.created_at).toLocaleString("en-IN")} · {PAY[order.payment_status]} · <span className="capitalize">{order.status}</span></div>
            <div className="divide-y divide-sharoa-line border-y border-sharoa-line">
              {order.items.map((it) => (
                <div key={it.sku} className="flex gap-3 py-3">
                  <img src={it.image} alt="" className="w-14 h-18 object-cover bg-sharoa-ivory" />
                  <div className="flex-1">
                    <p className="font-serif text-sm">{it.name}</p>
                    <p className="text-xs text-sharoa-muted">{it.size}·{it.color}·Qty {it.quantity} · {money(it.price)}</p>
                    {it.return_open && <button data-testid={`return-item-${it.sku}`} onClick={() => setRet(it)} className="mt-1.5 text-xs text-sharoa-rose sh-underline">Return Item · {it.days_left} days left</button>}
                    {order.status === "delivered" && !it.return_eligible && <span className="mt-1.5 block text-xs text-sharoa-muted">Final Sale · Not returnable</span>}
                    {order.status === "delivered" && it.return_eligible && !it.return_open && <span className="mt-1.5 block text-xs text-sharoa-muted">Return window closed</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-sharoa-ivory p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Total</span><span className="font-mono">{money(order.total)}</span></div>
              <p className="text-xs text-sharoa-muted">{order.address?.house}, {order.address?.street}, {order.address?.city} — {order.address?.pincode}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="font-serif text-lg">Return: {ret.name}</p>
            <div><span className="text-xs uppercase tracking-widest text-sharoa-muted">Reason</span>
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full border border-sharoa-line px-3 py-2.5 text-sm mt-1">
                {REASONS.map((r) => <option key={r}>{r}</option>)}
              </select></div>
            <div><span className="text-xs uppercase tracking-widest text-sharoa-muted">Resolution</span>
              <div className="flex gap-2 mt-1">
                {["refund", "exchange", "store_credit"].map((r) => (
                  <button key={r} onClick={() => setForm({ ...form, resolution: r })} className={`flex-1 text-xs uppercase tracking-widest py-2.5 border capitalize ${form.resolution === r ? "bg-sharoa-charcoal text-white border-sharoa-charcoal" : "border-sharoa-line"}`}>{r.replace("_", " ")}</button>
                ))}
              </div></div>
            <textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} placeholder="Add comments (optional)" className="w-full border border-sharoa-line px-3 py-2.5 text-sm h-20" />
            <div className="flex gap-2">
              <button onClick={() => setRet(null)} className="border border-sharoa-line text-xs uppercase tracking-widest px-4 py-3">Back</button>
              <button data-testid="submit-return" onClick={submitReturn} className="flex-1 bg-sharoa-rose text-white text-xs uppercase tracking-widest py-3">Submit Return Request</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Returns() {
  const [rets, setRets] = useState([]);
  useEffect(() => { api.get("/returns").then((r) => setRets(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="font-serif text-2xl mb-5">My Returns</h2>
      {rets.length === 0 ? <p className="text-sharoa-muted text-sm">No return requests yet.</p> : (
        <div className="space-y-4">
          {rets.map((r) => (
            <div key={r.id} className="border border-sharoa-line p-4">
              <div className="flex justify-between">
                <div><p className="font-mono text-sm">#{r.return_number}</p><p className="text-xs text-sharoa-muted">Order #{r.order_number}</p></div>
                <span className="text-xs text-sharoa-rose capitalize">{r.status.replace(/_/g, " ")}</span>
              </div>
              <p className="text-sm mt-2">{r.items?.map((i) => i.name).join(", ")}</p>
              <p className="text-xs text-sharoa-muted mt-1">Reason: {r.reason} · Deadline: {r.return_deadline}</p>
              <Link to={`/returns/${r.id}`} data-testid={`view-return-${r.return_number}`} className="inline-block mt-3 border border-sharoa-charcoal text-xs uppercase tracking-widest px-4 py-2">View Return</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Wishlist() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="font-serif text-2xl mb-5">Wishlist ♡</h2>
      {items.length === 0 ? <p className="text-sharoa-muted text-sm">Your wishlist is empty.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{items.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      )}
    </div>
  );
}

function Addresses({ user, onSave }) {
  const [list, setList] = useState(user.addresses || []);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", house: "", street: "", area: "", city: "", state: "", pincode: "", country: "India" });
  const add = async () => { try { const r = await api.post("/addresses", f); setList(r.data); setAdding(false); onSave(); toast("Address saved ♡"); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { const r = await api.delete(`/addresses/${id}`); setList(r.data); onSave(); };
  return (
    <div>
      <div className="flex justify-between items-center mb-5"><h2 className="font-serif text-2xl">Saved Addresses</h2>
        <button onClick={() => setAdding((v) => !v)} className="flex items-center gap-1 text-xs uppercase tracking-widest text-sharoa-rose"><Plus size={14} /> Add</button></div>
      {adding && (
        <div className="border border-sharoa-line p-4 mb-4 grid grid-cols-2 gap-2">
          {[["name", "Name"], ["phone", "Phone"], ["house", "House/Flat"], ["street", "Street"], ["area", "Area"], ["city", "City"], ["state", "State"], ["pincode", "PIN"]].map(([k, l]) => (
            <input key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="border border-sharoa-line px-3 py-2 text-sm" />
          ))}
          <button onClick={add} className="col-span-2 bg-sharoa-rose text-white text-xs uppercase tracking-widest py-2.5">Save Address</button>
        </div>
      )}
      <div className="space-y-3">
        {list.map((a) => (
          <div key={a.id} className="border border-sharoa-line p-4 flex justify-between">
            <div className="text-sm"><p className="font-medium">{a.name} · {a.phone}</p><p className="text-sharoa-muted">{a.house}, {a.street}, {a.area} {a.city}, {a.state} — {a.pincode}</p></div>
            <button onClick={() => del(a.id)}><Trash2 size={15} className="text-sharoa-muted hover:text-sharoa-rose" /></button>
          </div>
        ))}
        {list.length === 0 && !adding && <p className="text-sharoa-muted text-sm">No saved addresses.</p>}
      </div>
    </div>
  );
}
